import React from 'react';
import { Container } from '../landingPage/components/Container';

export default function TermsPage() {
  return (
    <Container className="py-16">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900 mb-8">Terms and Conditions</h1>
        <p className="text-sm text-gray-600 mb-8">Last updated: 03/10/2024</p>

        <div className="prose prose-sm max-w-none">
          <p className="mb-8">
            By consenting to the sharing of your personal data, you agree to the following terms and conditions set by EnableSEND Ltd ("we," "our," "us"). 
            This document outlines how your data will be shared, used, and protected. For further information please refer to our privacy policy.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Introduction</h2>
          <p>
            Welcome to EnableSEND.co.uk (the "Website"). These Terms and Conditions ("T&Cs") govern your use of our subscription services ("Services"). 
            By subscribing to our Services, you agree to comply with and be bound by these T&Cs. If you do not agree with these T&Cs, please do not use our Services.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Subscription Services</h2>
          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Subscription Plans</h3>
          <p>
            We offer various subscription plans for our Services. Details of these plans, including pricing and features, are available on our Website.
          </p>
          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Account Registration</h3>
          <p>
            To subscribe to our Services, you must create an account by providing accurate and complete information. 
            You are responsible for maintaining the confidentiality of your account information and password.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Booking Services</h2>
          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Booking Fee</h3>
          <p>
            We charge a 7.5% booking fee (including stripe processing fee) on all bookings made through our booking platform, this will be removed after point of sale.
          </p>

          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Provider Booking Services and Cancellation Policy</h3>
          <p>Our cancellation policy is as follows:</p>
          <ul className="list-disc pl-6 mb-4">
            <li className="mb-2">Any cancellations at least 24 hours before the session date will be refunded. An alternative session date should try to be agreed where possible before a cancellation and refund is processed.</li>
            <li className="mb-2">Cancellations within 24 hours or after the session date will not be refunded unless the provider agrees to this.</li>
            <li className="mb-2">Any cancellations within 24 hours or after the session date will be at the discretion of the provider as to whether a refund can be made. This is why we suggest you include your cancellation policy for sessions cancelled within 24 hours or during/after the session date.</li>
            <li className="mb-2">We would encourage every provider to be as flexible as possible if a child cannot attend a session for a valid reason.</li>
            <li className="mb-2">Any agreed refunds need to be cancelled on the booking system and then an email sent to customerservices@enablesend.co.uk</li>
            <li>Once a parent books with you – we will send funds to the bank account the provider entered on sign up a maximum of every 30 calendar days after the event has taken place. This will be the booking amount minus the booking fee.</li>
          </ul>

          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">User Booking Services and Cancellation Policy</h3>
          <p>When making bookings please read the providers guidance within the specific booking.</p>
          <p className="mb-4">For rearranging sessions please contact providers through the messaging system or directly if you would like to arrange an alternative date than the one you have already booked.</p>
          <p>Our cancellation policy is as follows:</p>
          <ul className="list-disc pl-6 mb-4">
            <li className="mb-2">If you cancel at least 24 hours before the session date our terms are to refund you. An alternative session date should try to be agreed where possible before a cancellation and refund is processed. You should contact the provider to let them know of the cancellation.</li>
            <li className="mb-2">Any cancellations within 24 hours or after the session date will be at the discretion of the provider as to whether a refund can be made. Please refer to the providers cancellation policy within the specific session being booked.</li>
            <li className="mb-2">We encourage providers to be as flexible as possible if a child cannot attend a session for a valid reason, but we cannot guarantee a refund on your behalf. This is an arrangement between yourselves and the provider.</li>
          </ul>
          <p>Any queries please contact us on customerservices@enablesend.co.uk</p>
          <p>Any refund will be the cost of the session minus the 7.5% booking fee.</p>

          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">User and Provider Liability</h3>
          <p>
            We hold no liability for resolving reimbursement issues between the provider (organisation) and the user (parent/carer) or vice versa. 
            We are not responsible for ensuring the user (parent/carer) attends the session, nor that the provider (organisation) ensures the session runs as planned or advertised.
          </p>
          <p>We are not responsible for misrepresentation of sessions on the booking platform.</p>
          <p>All subscribers to EnableSEND use the booking platform at their own discretion and risk.</p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Payment Terms</h2>
          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Billing</h3>
          <p>
            By subscribing to our Services, you agree to pay the subscription fees as specified during the registration process. 
            Payments will be billed in advance on a recurring basis (e.g., monthly or annually).
          </p>

          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Payment Methods</h3>
          <p>
            We accept various payment methods, including credit/debit cards and other online payment options. 
            By providing payment information, you authorise us to charge the applicable fees to your chosen payment method.
          </p>

          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Refunds</h3>
          <p>
            Subscription fees are non-refundable except as required by law or as otherwise specified in these T&Cs. 
            Under the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013, 
            you have the right to cancel your subscription within 14 days of signing up for a full refund.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">User Obligations</h2>
          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Compliance</h3>
          <p>You agree to use the Services in compliance with all applicable laws and regulations.</p>

          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Prohibited Activities</h3>
          <p>You agree not to engage in any activities that are illegal, harmful, or violate the rights of others, including but not limited to:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Sharing your account with others.</li>
            <li>Using the Services for fraudulent or malicious purposes.</li>
            <li>Accessing or attempting to access other users' accounts.</li>
          </ul>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Intellectual Property</h2>
          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Ownership</h3>
          <p>
            All content, trademarks, and intellectual property on the Website are owned by or licensed to EnableSEND.co.uk also known as EnableSEND ltd. 
            You may not use, reproduce, or distribute any content without our prior written permission.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Privacy Policy</h2>
          <p>
            Your use of the Services is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal information. 
            By using our Services, you consent to the practices described in the Privacy Policy.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Termination</h2>
          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Termination by You</h3>
          <p>
            You may cancel your subscription at any time through your account settings. 
            Upon cancellation, you will continue to have access to the Services until the end of your current billing period.
          </p>

          <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">Termination by Us</h3>
          <p>
            We reserve the right to suspend or terminate your account and access to the Services if you violate these T&Cs or engage in any activities that may harm our interests or the interests of other users. 
            We may provide reasonable notice before termination unless the violation is severe, in which case immediate termination may be warranted.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, EnableSEND.co.uk also known as EnableSEND ltd shall not be liable for any indirect, incidental, special, or consequential damages arising out of or in connection with your use of the Services. 
            This does not affect your statutory rights as a consumer under UK law.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Changes to T&Cs</h2>
          <p>
            We reserve the right to update or modify these T&Cs at any time. Any changes will be effective immediately upon posting on the Website. 
            Your continued use of the Services after such changes constitutes your acceptance of the new T&Cs. 
            We will notify you of significant changes in advance.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Governing Law</h2>
          <p>
            These T&Cs shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law principles. 
            Any disputes arising under or in connection with these T&Cs shall be subject to the exclusive jurisdiction of the courts of England and Wales.
          </p>

          <h2 className="text-2xl font-semibold text-gray-900 mt-8 mb-4">Contact Information</h2>
          <p>If you have any questions or concerns about these T&Cs, please contact us at:</p>
          <p className="text-blue-600">customerservices@enablesend.co.uk</p>
        </div>
      </div>
    </Container>
  );
} 